import React, { DetailedHTMLProps, ForwardedRef, InputHTMLAttributes } from "react";
import { useTranslation } from "react-i18next";
import theme from "@monorepo/styles/theme";
import { ParamLabelEnum } from "@monorepo/types";
import { Info } from "@mui/icons-material";
import { Box, InputLabel, styled, SxProps, Tooltip, Typography } from "@mui/material";

type TProps = DetailedHTMLProps<InputHTMLAttributes<HTMLInputElement>, HTMLInputElement> & {
  label?: string;
  error?: string;
  icon?: React.ReactNode;
  sx?: SxProps;
  autoFocus?: boolean;
  required?: boolean;
  isThirdPartyPE?: boolean;
  isCounter?: boolean;
  inputVal?: string;
  isAMT?: boolean;
  tooltipMessage?: string;
};

export const StyledInput = styled("input", {
  shouldForwardProp: (prop) => prop !== "withIcon" && prop !== "largerFont" && prop !== "greenBottomBorder",
})<{
  withIcon?: boolean;
  largerFont?: boolean;
  greenBottomBorder?: boolean;
}>(({ withIcon, largerFont, greenBottomBorder }) => ({
  fontSize: largerFont ? theme.typography.body1.fontSize : theme.typography.body2.fontSize,
  backgroundColor: "transparent",
  padding: withIcon ? "6px 30px 3px 3px" : "6px 6px 3px 3px",
  boxSizing: "border-box",
  borderRadius: 0,
  oultine: 0,
  border: 0,
  borderBottom: `1px solid ${greenBottomBorder ? theme.palette.primary.main : theme.palette.geyserGray.main}`,
  width: "100%",
  "&:selection": {
    background: theme.palette.primary.main,
    color: theme.palette.text.secondary,
  },
  "&::placeholder": {
    color: theme.palette.nobelGray.main,
  },
  "&:read-only, &:disabled": {
    pointerEvents: "none",
    background: theme.palette.background.default,
    color: theme.palette.text.disabled,
    borderBottom: "0!important",
  },
  "&:focus, :focus-visible": {
    outline: 0,
    borderBottom: `1px solid ${greenBottomBorder ? theme.palette.primary.dark : theme.palette.primary.main}!important`,
    "& .MuiSvgIcon-root": {
      color: theme.palette.primary.main,
      stroke: theme.palette.primary.main,
    },
  },
  ...(greenBottomBorder && {
    "&:hover:not(:read-only), &:hover:not(:disabled)": {
      borderBottom: `1px solid ${
        greenBottomBorder ? theme.palette.primary.dark : theme.palette.primary.main
      }!important`,
    },
  }),
}));

export const Input = React.forwardRef((props: TProps, ref: ForwardedRef<HTMLInputElement>) => {
  const {
    required,
    className,
    label,
    id,
    icon,
    readOnly,
    disabled,
    error,
    autoFocus,
    isThirdPartyPE,
    isCounter,
    inputVal,
    isAMT,
    sx,
    tooltipMessage,
    ...otherProps
  } = props;
  const { t } = useTranslation();
  return (
    <Box
      sx={{
        position: "relative",
        display: "flex",
        flexDirection: "column",
        rowGap: "6px",
        ...sx,
      }}
    >
      {label && (
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <InputLabel
            sx={{
              whiteSpace: "break-spaces",
              fontSize: theme.typography.inputLabel.fontSize,
              padding: "6px 6px 3px 3px",
              textAlign: "left",
              "&::after": {
                content: required ? "' *'" : "''",
                color: theme.palette.error.dark,
              },
            }}
            htmlFor={id}
          >
            {label}
          </InputLabel>
          {tooltipMessage ? (
            <Tooltip
              title={
                <Typography
                  sx={{ fontSize: "inherit" }}
                  dangerouslySetInnerHTML={{ __html: String(t(tooltipMessage)) }}
                />
              }
            >
              <Info sx={{ stroke: "none", fill: theme.palette.primary.main }} />
            </Tooltip>
          ) : null}
        </Box>
      )}
      <Box
        sx={{
          position: "relative",
        }}
      >
        <StyledInput
          sx={sx}
          className={className}
          withIcon={!!icon}
          greenBottomBorder={(!!isCounter || !!isAMT) && (!inputVal || inputVal === "0")}
          largerFont={isThirdPartyPE && label?.toLowerCase()?.includes(ParamLabelEnum.TARIFF)}
          {...otherProps}
          id={id}
          readOnly={readOnly}
          disabled={disabled}
          ref={ref}
          autoFocus={!!autoFocus}
        />
        {icon && (
          <Box
            sx={{
              color: theme.palette.text.secondary,
              stroke: theme.palette.shuttleGray.main,
              position: "absolute",
              right: "0",
              bottom: "0",
            }}
          >
            {icon}
          </Box>
        )}
      </Box>
      {error && (
        <InputError>
          <Typography>{error}</Typography>
        </InputError>
      )}
    </Box>
  );
});

export const InputGroup = ({ children, error }: { children: React.ReactNode; error?: string }) => {
  return (
    <Box sx={{ display: "flex", flexDirection: "column", rowGap: "6px" }}>
      <Box sx={{ display: "flex", flexWrap: "wrap", rowGap: "6px" }}>{children}</Box>
      {error && (
        <Typography
          component="span"
          sx={{
            marginTop: "3px",
            textAlign: "left",
            color: theme.palette.error.main,
            backgroundColor: "transparent",
          }}
        >
          {error}
        </Typography>
      )}
    </Box>
  );
};

export const InputAddon = ({ children, sx }: { children: React.ReactNode; sx?: SxProps }) => {
  return <Box sx={{ padding: "0px 6px", ...sx }}>{children}</Box>;
};

export const InputError = ({ children, sx }: { children: React.ReactNode; sx?: SxProps }) => {
  return (
    <Box
      sx={{
        marginTop: "3px",
        textAlign: "left",
        color: theme.palette.error.main,
        backgroundColor: "transparent",
        ...sx,
      }}
    >
      {children}
    </Box>
  );
};
